/**
 * Merci : la page d'un set de produits.
 *
 * Deux composants, rendus par `sections/merci-set.liquid` :
 *
 *   - `merci-set-media` marque le point courant de la galerie en mobile. Le
 *     défilement est natif — `scroll-snap` —, et les points ne disent rien
 *     qu'on ne voie déjà : sans script, ils restent sur la première image.
 *   - `merci-set-buy` est le configurateur : il tient la sélection, et l'ajoute
 *     au panier en une requête.
 *
 * --- On compose, puis on ajoute
 *
 * Fonctionnement voulu par Gabriel le 2026-10-05. Rien n'est sélectionné au
 * chargement, et le bouton du bas affiche « Faites votre sélection », inactif.
 *
 *   - le « + » d'une ligne la sélectionne à 1 : il passe en rouge et le choix de
 *     la quantité apparaît. Un nouveau clic sur le « + » rouge ajoute une unité ;
 *   - le « − » du choix de quantité retire une unité ; à zéro, la ligne sort de
 *     la sélection, le choix de quantité se masque et le « + » repasse en blanc ;
 *   - la quantité s'arrête au stock quand la ligne en porte un (`data-max`) ;
 *   - le bouton affiche « Ajouter au panier » et le total de la sélection dès
 *     qu'une ligne est choisie.
 *
 * La sélection vit dans les lignes elles-mêmes (`data-quantity`), pas dans un
 * état à part : ce que l'on voit est ce que l'on envoie.
 *
 * --- L'ajout
 *
 * La requête est celle d'Horizon pour un ajout groupé (`#processBatchAddToCart`
 * de `product-form.js`, privé, donc recopié dans ses grandes lignes) : un
 * `CartLinesUpdateEvent` émis avant l'envoi, résolu avec le panier et les
 * sections rendues. C'est cet événement qu'écoutent le tiroir panier et le
 * compteur de l'en-tête : on n'a rien à leur dire de plus.
 *
 * Chaque produit porte la propriété cachée `_set`, le nom du set — invisible au
 * panier comme au paiement, conservée dans la commande.
 *
 * Après un ajout réussi, la sélection repart à zéro : un second clic ne doit pas
 * ajouter tout le set une seconde fois sans qu'on le voie. Si Shopify refuse —
 * un stock épuisé entre le chargement et le clic —, rien n'est ajouté, la
 * sélection reste, et le message s'affiche sous le bouton.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

class MerciSetMedia extends HTMLElement {
  #observer;

  connectedCallback() {
    const track = this.querySelector('.merci-set__track');
    const dots = [...this.querySelectorAll('.merci-set__dot')];
    if (!track || dots.length === 0) return;

    const slides = [...track.querySelectorAll('.merci-set__slide')];

    // Une image est courante quand elle occupe plus de la moitié de la piste :
    // une seule à la fois, les images faisant toute la largeur en mobile.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(entry.target);
          dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const slide of slides) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

class MerciSetBuy extends HTMLElement {
  connectedCallback() {
    this.set = this.closest('.merci-set');
    this.button = this.querySelector('[ref="button"]');
    this.label = this.querySelector('[ref="label"]');
    this.total = this.querySelector('[ref="total"]');
    this.error = this.querySelector('[ref="error"]');

    this.set?.addEventListener('click', this.#surClic);
    this.button?.addEventListener('click', this.#ajouter);
    this.#rafraichir();
  }

  disconnectedCallback() {
    this.set?.removeEventListener('click', this.#surClic);
    this.button?.removeEventListener('click', this.#ajouter);
  }

  /** Les lignes sélectionnables du set. */
  #lignes() {
    return this.set ? [...this.set.querySelectorAll('.merci-set-item[data-selectable]')] : [];
  }

  #quantite(ligne) {
    return Number(ligne.dataset.quantity) || 0;
  }

  /** Un clic sur un « + » ou un « − » d'une ligne. */
  #surClic = (event) => {
    const bouton = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    const ligne = bouton?.closest('.merci-set-item[data-selectable]');
    if (!bouton || !ligne || bouton.disabled) return;

    this.#regler(ligne, this.#quantite(ligne) + Number(bouton.dataset.step));
    this.#afficherErreur('');
  };

  /** Pose la quantité d'une ligne, bornée entre zéro et le stock. */
  #regler(ligne, quantite) {
    const max = ligne.dataset.max === '' ? Infinity : Number(ligne.dataset.max);
    const valeur = Math.max(0, Math.min(quantite, max));
    const choisie = valeur > 0;

    ligne.dataset.quantity = String(valeur);
    ligne.toggleAttribute('data-selected', choisie);

    const qty = ligne.querySelector('.merci-set-item__qty');
    if (qty instanceof HTMLElement) qty.hidden = !choisie;

    const compteur = ligne.querySelector('.merci-set-item__count');
    if (compteur) compteur.textContent = String(valeur);

    const ajout = ligne.querySelector('.merci-set-item__add');
    ajout?.setAttribute('aria-pressed', String(choisie));

    // Au stock atteint, plus rien à ajouter : les deux « + » se désactivent.
    for (const plus of ligne.querySelectorAll('[data-step="1"]')) {
      if (plus instanceof HTMLButtonElement) plus.disabled = valeur >= max;
    }

    this.#rafraichir();
  }

  /** Le libellé et le total du bouton, selon la sélection. */
  #rafraichir() {
    if (!this.button) return;

    const choisies = this.#lignes().filter((ligne) => this.#quantite(ligne) > 0);
    const total = choisies.reduce(
      (somme, ligne) => somme + Number(ligne.dataset.price || 0) * this.#quantite(ligne),
      0
    );
    const vide = choisies.length === 0;

    this.button.disabled = vide;
    if (this.label) {
      this.label.textContent = vide ? this.dataset.labelSelect || '' : this.dataset.labelAdd || '';
    }
    if (this.total) {
      this.total.hidden = vide;
      this.total.textContent = vide
        ? ''
        : formatMoney(total, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
    }
  }

  #ajouter = async () => {
    const lignes = this.#lignes()
      .filter((ligne) => this.#quantite(ligne) > 0 && ligne.dataset.variantId)
      .map((ligne) => ({ variantId: ligne.dataset.variantId, quantity: this.#quantite(ligne) }));
    if (lignes.length === 0 || !this.button) return;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.#afficherErreur('');

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((composant) => composant instanceof HTMLElement && composant.dataset.sectionId)
      .filter(Boolean);

    const differe = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lignes.map((ligne) => ({ merchandiseId: ligne.variantId, quantity: ligne.quantity })),
        promise: differe.promise,
      })
    );

    let reussi = false;
    try {
      const proprietes = this.dataset.set ? { _set: this.dataset.set } : undefined;
      const reponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lignes.map((ligne) => ({
            id: Number(ligne.variantId),
            quantity: ligne.quantity,
            ...(proprietes && { properties: proprietes }),
          })),
          sections: sections.join(','),
        }),
      });
      const donnees = await reponse.json();
      const panier = await this.#panier();

      if (donnees.status) {
        this.dispatchEvent(new CartErrorEvent({ error: donnees.message || 'Add to cart failed', code: 'INVALID' }));
        differe.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
          detail: { items: panier.items, source: 'merci-set', didError: true },
        });
        this.#afficherErreur(donnees.description || donnees.message || '');
        return;
      }

      differe.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
        detail: {
          items: panier.items,
          source: 'merci-set',
          itemCount: lignes.reduce((somme, ligne) => somme + ligne.quantity, 0),
          sections: donnees.sections,
          didError: false,
        },
      });
      reussi = true;
    } catch (erreur) {
      console.error(erreur);
      differe.reject(erreur);
      this.dispatchEvent(new CartErrorEvent({ error: 'Add to cart failed', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.button.removeAttribute('aria-busy');
      if (reussi) {
        for (const ligne of this.#lignes()) this.#regler(ligne, 0);
      } else {
        this.#rafraichir();
      }
    }
  };

  async #panier() {
    const reponse = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!reponse.ok) throw new Error(`Failed to fetch cart: ${reponse.status}`);
    return reponse.json();
  }

  #afficherErreur(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = message === '';
  }
}

if (!customElements.get('merci-set-media')) customElements.define('merci-set-media', MerciSetMedia);
if (!customElements.get('merci-set-buy')) customElements.define('merci-set-buy', MerciSetBuy);
